import React, {useMemo, useState} from "react";
import {Button, Checkbox, Col, Dropdown, Menu, Row, Tooltip} from "antd";
import {ChevronRightIcon, DiamondIcon, FlameIcon, PriceListIcon, StorageBoxOutlineIcon} from "@components/icons/Icons";
import {FmvPrice} from "@components/fmv-price";
import {KeyIssueBadge} from "@components/key-issue-badge";

import ebay_logo from "@images/ebay_logo.png";
import {getMyCollectionUrlWithIssueId} from "@lib/Routes";
import {Link} from "react-router-dom";
import {EllipsisOutlined} from "@ant-design/icons";
import {formatDateYear, getEbayTitleFromIssue} from "@lib/format";
import {RenderHtml} from "@components/html";
import {ComicGradeSelect, getDefaultFmvDataFromIssue} from "@components/comic-grade-select";
import BookCover from "@components/book-cover";

const IssuesListItemDesktop = ({
    item,
    showEditMultiple,
    isSelected,
    toggleSelected,
    onClick,
    issueDate,
    showValueRibbon,
    allowUnlimited,
    navigate,
    windowDimensions,
    wishlistIssueAdd,
    wishlistIssueRemove,
    setAddIssueId,
    isLoggedIn,
    showListViewNoImages,
    setPriceListId,
    listViewColumns,
    showIssueNotes,
    shopLink,
}) => {

    const smallButtons = useMemo(() => {
        return windowDimensions.under(1200);
    }, [windowDimensions]);

    const ebayTitle = useMemo(() => getEbayTitleFromIssue(item), [item]);
    const [fmvData, setFmvData] = useState(getDefaultFmvDataFromIssue(item));

    return (
        <div className="grid-list-item">
            <Row className={"grid-list-item-inner"}>
                {(showEditMultiple && toggleSelected) && (
                    <Col span={listViewColumns.editMultiple} className={"col-cbx flex-align-center "}>
                        <Checkbox checked={isSelected} onChange={() => toggleSelected(item.id)}/>
                    </Col>
                )}

                {!showListViewNoImages && (
                    <Col span={listViewColumns.bookImage} >
                        <div className="col-image">
                            <BookCover issue={item} className="list-book-cover" preview={true} />
                        </div>
                    </Col>
                )}

                <Col span={listViewColumns.issueDetails} className={"pad-left-20 flex-vertical-center"} style={{minHeight: 20}}>
                    <div>
                        <div className={"valueBadges no-wrap mar-bot-5 flex-row"}>
                            {item.is_key && (
                                <KeyIssueBadge keyInfo={item.key_info} windowDimensions={windowDimensions}
                                               isLargeScreen={true}/>)}
                            {item.is_hot && (<div className="valueBadge hotIssue isActive"><FlameIcon/></div>)}
                            {item.is_rare && (<div className="valueBadge rareIssue isActive"><DiamondIcon/></div>)}
                            {item.total_in_collection > 0 && (
                                <div className={"mar-top-3 mar-left-10"}>
                                    <Link to={getMyCollectionUrlWithIssueId(item.id)}>
                                        <span className="issues-owned">
                                            <StorageBoxOutlineIcon/><strong>&nbsp;:&nbsp;<span
                                            className="total-number">{item.total_in_collection}</span></strong>
                                        </span>
                                    </Link>
                                </div>
                            )}
                        </div>

                        <div className="col-issue-desc">
                            <div className="cursor-pointer titleLink font-16">
                                <a href={`/issue/${item.slug}`}>{item.series_title} {item.issue_number}</a>
                            </div>
                            <div
                                className="font-14 font-normal">{(item.variant_name && item.variant_name !== '') ? item.variant_name : 'Regular'}</div>
                            <div className="font-14 font-italic">{item.publisher}</div>
                            {showIssueNotes && (
                                <div style={{fontWeight: 'normal'}}><RenderHtml>{item.issue_notes}</RenderHtml></div>
                            )}
                        </div>
                    </div>
                </Col>

                <Col className="no-wrap flex-vertical-center flex-align-center" span={listViewColumns.year}>
                {formatDateYear(issueDate)}
                </Col>

                <Col className="no-wrap flex-vertical-center flex-align-center" span={listViewColumns.grade}>
                    <ComicGradeSelect issue={item} onChange={setFmvData} />
                </Col>
                <Col className="no-wrap flex-vertical-center flex-align-center" span={listViewColumns.rawFmv}>
                    <div className="mar-auto-0 flex" style={{color: '#4C8391'}}>
                        {showValueRibbon ? (
                            <FmvPrice
                                prefix={fmvData.raw.grade ? fmvData.raw.grade + ' | ' : null}
                                value={fmvData.raw.value}
                                stale={fmvData.raw.stale}
                            />
                        ) : (
                            <div>
                                <a className="upgrade-link" href="/plans/unlimited/">Upgrade</a>
                            </div>
                        )}
                    </div>
                </Col>
                <Col className="no-wrap flex-vertical-center flex-align-center" span={listViewColumns.slabFmv}>
                    <div className="mar-auto-0 flex" style={{color: '#34A09A'}}>
                        {showValueRibbon ? (
                            <FmvPrice
                                prefix={fmvData.slabbed.grade ? fmvData.slabbed.grade + ' | ' : null}
                                value={fmvData.slabbed.value}
                                stale={fmvData.slabbed.stale}
                            />
                        ) : (
                            <div>
                                <a className="upgrade-link" href="/plans/unlimited/">Upgrade</a>
                            </div>
                        )}
                    </div>
                </Col>

                <Col className="no-wrap flex-vertical-center flex-align-center" span={listViewColumns.ebay}>
                    {shopLink ? (
                        <a href={shopLink.link_url} target="_blank" className={`ebay-link ${shopLink.className || ''}`}>
                            <img src={shopLink.image} className={"ebay-logo"}/>
                        </a>
                        ) : (
                        <a
                            href={`https://www.ebay.com/sch/63/i.html?_from=R40&_nkw=${ebayTitle}&mkcid=1&mkrid=711-53200-19255-0&siteid=0&campid=5339059464&toolid=20008&mkevt=1`}
                            target="_blank" className="ebay-link"
                        >
                            <img src={ebay_logo} className={"ebay-logo"}/>
                        </a>
                    )}
                </Col>

                <Col className="no-wrap flex-vertical-center flex-align-center" span={listViewColumns.priceTable}>
                    <Tooltip title="Show Price Table">
                        {smallButtons ? (
                            <Button
                                className="greyButton rounded-3px"
                                size={smallButtons && "small"}
                                onClick={() => setPriceListId(item.id)}
                                icon={<PriceListIcon className={`icon16 iconDarkGrey text-light-blue`}/>}
                            />
                        ) : (
                            <Button
                                onClick={() => setPriceListId(item.id)}
                                className={'greyButton text-light-blue rounded-3px'}
                            >Price Table</Button>
                        )}
                    </Tooltip>
                </Col>

                <Col className="no-wrap flex-vertical-center flex-align-center" span={listViewColumns.actions}>
                    <Row className="issue-actions" justify="center">
                        <Dropdown className="greyButton selectButton rounded-3px" trigger={["click"]} overlay={(
                            <Menu>
                                {isLoggedIn && (
                                    <>
                                        <Menu.Item key={1} onClick={() => setAddIssueId(item.id)}>Add to Collection</Menu.Item>
                                        <Menu.Item key={2} onClick={() => {
                                            if (item.in_wishlist) {
                                                wishlistIssueRemove([item.id]);
                                            } else {
                                                wishlistIssueAdd([item.id]);
                                            }
                                        }}>{item.in_wishlist ? "Remove from Wishlist" : "Add to Wishlist"}</Menu.Item>
                                    </>
                                )}
                                <Menu.Item key={3}>
                                    <a href={`/marketplace/?issue_id=${item.id}`}>Buy In Marketplace</a>
                                </Menu.Item>
                                <Menu.Item key={4}>
                                    <a href={`/issue/${item.slug}`}>Go to Price Guide</a>
                                </Menu.Item>
                            </Menu>
                        )}>
                            <Tooltip title="Actions">
                                <Button className="greyButton rounded-3px" size={smallButtons && "small"}>
                                    <EllipsisOutlined className="icon16 iconCovrBlueLight" />
                                </Button>
                            </Tooltip>
                        </Dropdown>
                    </Row>
                </Col>

                <Col className="no-wrap flex-vertical-center flex-align-right" span={listViewColumns.issueLink}>
                    <Tooltip title="Go to Price Guide">
                        <Button type="text" size={smallButtons ? "middle" : "large"} className="pad-0" href={`/issue/${item.slug}`}>
                            <ChevronRightIcon className="icon30 iconGrey"/>
                        </Button>
                    </Tooltip>
                </Col>
            </Row>
        </div>
    );
};

export default IssuesListItemDesktop;